import React, { useMemo, useCallback } from "react";
import { useCookiePreferences } from "../../hooks/useCookiePreferences";
import { useScratchpadPersistence } from "../../hooks/useScratchpadPersistence";
import "./Scratchpad.css";

// Lazy load ReactQuill for performance
const ReactQuill = React.lazy(() => import("react-quill"));

interface ScratchpadProps {
	isEmbedded?: boolean;
}

const Scratchpad: React.FC<ScratchpadProps> = ({ isEmbedded = false }) => {
	const { isDarkMode } = useCookiePreferences();
	const {
		content,
		updateContent,
		clearContent: clearPersistedContent,
		isLoaded,
	} = useScratchpadPersistence();

	// Memoized Quill modules configuration
	const modules = useMemo(
		() => ({
			toolbar: [
				[{ header: [1, 2, 3, false] }],
				["bold", "italic", "underline", "strike"],
				[{ list: "ordered" }, { list: "bullet" }],
				[{ color: [] }, { background: [] }],
				[{ align: [] }],
				["link"],
				["clean"],
			],
		}),
		[],
	);

	// Memoized Quill formats
	const formats = useMemo(
		() => [
			"header",
			"bold",
			"italic",
			"underline",
			"strike",
			"list",
			"bullet",
			"color",
			"background",
			"align",
			"link",
		],
		[],
	);

	// Download as text file
	const downloadAsText = useCallback(() => {
		// Create plain text from HTML content
		const tempDiv = document.createElement("div");
		tempDiv.innerHTML = content;
		const plainText = tempDiv.textContent || tempDiv.innerText || "";

		const blob = new Blob([plainText], { type: "text/plain" });
		const url = URL.createObjectURL(blob);
		const link = document.createElement("a");
		link.href = url;
		link.download = `scratchpad-${new Date().toISOString().split("T")[0]}.txt`;
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
		URL.revokeObjectURL(url);
	}, [content]);

	// Download as HTML file
	const downloadAsHtml = useCallback(() => {
		const htmlContent = `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Scratchpad Notes</title>
    <style>
        body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
            line-height: 1.6;
            max-width: 800px;
            margin: 0 auto;
            padding: 20px;
            color: #333;
        }
        .header {
            border-bottom: 2px solid #e5e7eb;
            padding-bottom: 10px;
            margin-bottom: 20px;
        }
        .date {
            color: #6b7280;
            font-size: 14px;
        }
    </style>
</head>
<body>
    <div class="header">
        <h1>Scratchpad Notes</h1>
        <div class="date">Generated on ${new Date().toLocaleDateString()}</div>
    </div>
    <div class="content">
        ${content}
    </div>
</body>
</html>`;

		const blob = new Blob([htmlContent], { type: "text/html" });
		const url = URL.createObjectURL(blob);
		const link = document.createElement("a");
		link.href = url;
		link.download = `scratchpad-${new Date().toISOString().split("T")[0]}.html`;
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
		URL.revokeObjectURL(url);
	}, [content]);

	// Clear content with confirmation
	const clearContent = useCallback(() => {
		if (
			content.trim() &&
			window.confirm("Are you sure you want to clear all content?")
		) {
			clearPersistedContent();
		}
	}, [content, clearPersistedContent]);

	// Handle content change
	const handleContentChange = useCallback(
		(value: string) => {
			updateContent(value);
		},
		[updateContent],
	);

	// Custom styles for the Quill editor
	const quillStyles = useMemo(
		() => ({
			height: "100%",
			display: "flex",
			flexDirection: "column" as const,
		}),
		[],
	);

	return (
		<div className="h-full flex flex-col">
			{/* Header */}
			<div className="flex items-center justify-between px-3 py-2 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-750">
				<h3 className="text-xs font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-2">
					<svg
						className="w-4 h-4"
						fill="none"
						stroke="currentColor"
						viewBox="0 0 24 24"
					>
						<path
							strokeLinecap="round"
							strokeLinejoin="round"
							strokeWidth="2"
							d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
						/>
					</svg>
					Scratchpad
				</h3>

				{/* Control buttons */}
				<div className="flex items-center gap-1">
					{/* Download as TXT */}
					<div className="relative group">
						<button
							onClick={downloadAsText}
							disabled={!content.trim()}
							className="text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors p-1 rounded-md hover:bg-blue-50 dark:hover:bg-blue-900/20"
							title="Download as Text"
						>
							<svg
								className="w-4 h-4"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
							>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth="2"
									d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
								/>
							</svg>
						</button>
					</div>

					{/* Download as HTML */}
					<div className="relative group">
						<button
							onClick={downloadAsHtml}
							disabled={!content.trim()}
							className="text-gray-500 dark:text-gray-400 hover:text-green-600 dark:hover:text-green-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors p-1 rounded-md hover:bg-green-50 dark:hover:bg-green-900/20"
							title="Download as HTML"
						>
							<svg
								className="w-4 h-4"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
							>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth="2"
									d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4"
								/>
							</svg>
						</button>
					</div>

					{/* Clear content */}
					{content.trim() && (
						<div className="relative group">
							<button
								onClick={clearContent}
								className="text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors p-1 rounded-md hover:bg-red-50 dark:hover:bg-red-900/20"
								title="Clear content"
							>
								<svg
									className="w-4 h-4"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth="2"
										d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
									/>
								</svg>
							</button>
						</div>
					)}
				</div>
			</div>

			{/* Editor Container */}
			<div className="flex-1 overflow-hidden">
				{!isLoaded ? (
					<div className="h-full flex items-center justify-center">
						<div className="text-center">
							<div className="w-8 h-8 mx-auto mb-2 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center animate-pulse">
								<svg
									className="w-4 h-4 text-gray-400 dark:text-gray-500"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth="2"
										d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
									/>
								</svg>
							</div>
							<p className="text-gray-500 dark:text-gray-400 text-xs">
								Loading content...
							</p>
						</div>
					</div>
				) : (
					<React.Suspense
						fallback={
							<div className="h-full flex items-center justify-center">
								<div className="text-center">
									<div className="w-8 h-8 mx-auto mb-2 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center animate-pulse">
										<svg
											className="w-4 h-4 text-gray-400 dark:text-gray-500"
											fill="none"
											stroke="currentColor"
											viewBox="0 0 24 24"
										>
											<path
												strokeLinecap="round"
												strokeLinejoin="round"
												strokeWidth="2"
												d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
											/>
										</svg>
									</div>
									<p className="text-gray-500 dark:text-gray-400 text-xs">
										Loading editor...
									</p>
								</div>
							</div>
						}
					>
						<div className="h-full">
							<ReactQuill
								theme="snow"
								value={content}
								onChange={handleContentChange}
								modules={modules}
								formats={formats}
								placeholder="Start writing your notes..."
								style={quillStyles}
								className={`quill-editor ${isDarkMode ? "dark" : ""}`}
							/>
						</div>
					</React.Suspense>
				)}
			</div>
		</div>
	);
};

export default Scratchpad;
